import { Redirect, Link } from "wouter";
import { ChevronLeft, Sliders, Wrench } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useMe } from "@/lib/queries";
import { PhysicsTunerControls } from "@/components/PhysicsTuner";

// Standalone tuner page. The same controls also surface as an in-game
// right-side dock during a virtual session (see PhysicsDock in
// components/PhysicsTuner.tsx) — both share the singleton store, so
// edits here apply to a live game and vice versa.
export default function AdminPhysics() {
  const { data: me, isLoading } = useMe();

  if (isLoading) {
    return <div className="p-8 text-muted-foreground text-sm">Loading…</div>;
  }
  if (!me) return <Redirect to="/sign-in" />;
  if (!me.isPlatformAdmin) {
    return (
      <div className="p-8 max-w-md mx-auto text-center space-y-3">
        <Wrench className="mx-auto text-muted-foreground" size={32} />
        <h1 className="text-2xl font-display uppercase tracking-tight">
          Restricted
        </h1>
        <p className="text-sm text-muted-foreground">
          Physics tuning is platform-admin only.
        </p>
        <Button asChild>
          <Link href="/dashboard">Back to dashboard</Link>
        </Button>
      </div>
    );
  }

  return (
    <article className="px-4 sm:px-8 py-6 sm:py-10 max-w-3xl mx-auto space-y-6">
      <header className="space-y-1">
        <div>
          <Link
            href="/admin"
            className="text-xs font-display uppercase tracking-widest text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
          >
            <ChevronLeft size={12} aria-hidden /> Admin
          </Link>
        </div>
        <p className="text-xs font-display uppercase tracking-widest text-muted-foreground">
          /admin/physics
        </p>
        <h1 className="text-3xl font-display uppercase tracking-tight inline-flex items-center gap-2">
          <Sliders className="text-primary" /> Physics tuning
        </h1>
        <p className="text-muted-foreground text-sm">
          Live knobs for the virtual cornhole renderer. Changes persist
          per-device and apply on the next frame — open{" "}
          <Link
            href="/dashboard/virtual"
            className="text-primary hover:underline"
            target="_blank"
          >
            /dashboard/virtual
          </Link>{" "}
          in another tab to test interactively, or open the in-game tuner
          dock (right-edge "Tune" tab) on the virtual game page directly.
        </p>
      </header>

      <PhysicsTunerControls />

      <footer className="pt-6 border-t border-border text-xs text-muted-foreground">
        Tuning values persist in{" "}
        <code className="bg-muted px-1 py-0.5 rounded">localStorage</code>.
        Defaults live in{" "}
        <code className="bg-muted px-1 py-0.5 rounded">
          apps/web/src/lib/virtual3d-tuning.ts
        </code>
        .
      </footer>
    </article>
  );
}
